import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { API_URL, Product } from '../types';
import { LatLng } from './LocationMap';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function createPriceIcon(priceLabel: string, selected: boolean): L.DivIcon {
  const selectedClass = selected ? ' map-price-marker--selected' : '';
  return L.divIcon({
    className: `map-price-marker${selectedClass}`,
    html: escapeHtml(priceLabel),
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
}

function createClusterIcon(count: number): L.DivIcon {
  return L.divIcon({
    className: 'map-cluster-icon-wrap',
    html: `<div class="map-cluster-marker">${count}</div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
}

export function productHasMapLocation(product: Product): boolean {
  return product.lenderApproximateLatitude != null && product.lenderApproximateLongitude != null;
}

interface FitResultsViewProps {
  points: LatLng[];
  searchCenter: LatLng;
  radiusKm: number;
}

const FitResultsView: React.FC<FitResultsViewProps> = ({ points, searchCenter, radiusKm }) => {
  const map = useMap();

  useEffect(() => {
    if (points.length === 0) {
      const radiusM = radiusKm * 1000;
      const padLat = radiusM / 111_320;
      const padLng = radiusM / (111_320 * Math.cos((searchCenter.lat * Math.PI) / 180));
      map.fitBounds(
        L.latLngBounds([
          [searchCenter.lat + padLat, searchCenter.lng + padLng],
          [searchCenter.lat - padLat, searchCenter.lng - padLng],
        ]),
        { padding: [28, 28], maxZoom: 14 },
      );
      return;
    }

    const boundsPoints: L.LatLngExpression[] = points.map(p => [p.lat, p.lng]);
    boundsPoints.push([searchCenter.lat, searchCenter.lng]);
    const bounds = L.latLngBounds(boundsPoints);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  }, [map, points, searchCenter, radiusKm]);

  return null;
};

interface ClusterMarkersProps {
  products: Product[];
  selectedProductId?: number;
  formatPrice: (product: Product) => string;
  onSelectProduct: (product: Product) => void;
  onHoverProduct: (product: Product | null, pos: { x: number; y: number } | null) => void;
}

const ClusterMarkers: React.FC<ClusterMarkersProps> = ({
  products,
  selectedProductId,
  formatPrice,
  onSelectProduct,
  onHoverProduct,
}) => {
  const map = useMap();

  useEffect(() => {
    const cluster = L.markerClusterGroup({
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      maxClusterRadius: 50,
      spiderLegPolylineOptions: { weight: 1.5, color: '#252629', opacity: 0.6 },
      iconCreateFunction: c => createClusterIcon(c.getChildCount()),
    });

    for (const product of products) {
      const lat = product.lenderApproximateLatitude!;
      const lng = product.lenderApproximateLongitude!;
      const selected = product.id === selectedProductId;
      const marker = L.marker([lat, lng], {
        icon: createPriceIcon(formatPrice(product), selected),
      });

      marker.on('click', () => onSelectProduct(product));
      marker.on('mouseover', () => {
        const point = map.latLngToContainerPoint([lat, lng]);
        onHoverProduct(product, { x: point.x, y: point.y });
      });
      marker.on('mouseout', () => onHoverProduct(null, null));
      cluster.addLayer(marker);
    }

    cluster.on('clustermouseover', () => onHoverProduct(null, null));
    map.addLayer(cluster);

    return () => {
      cluster.off('clustermouseover');
      map.removeLayer(cluster);
    };
  }, [map, products, selectedProductId, formatPrice, onSelectProduct, onHoverProduct]);

  return null;
};

export interface SearchResultsMapProps {
  products: Product[];
  searchCenter: LatLng;
  radiusKm: number;
  selectedProductId?: number;
  onSelectProduct: (product: Product) => void;
  formatPrice: (product: Product) => string;
  loading?: boolean;
}

export const SearchResultsMap: React.FC<SearchResultsMapProps> = ({
  products,
  searchCenter,
  radiusKm,
  selectedProductId,
  onSelectProduct,
  formatPrice,
  loading = false,
}) => {
  const [hoveredProduct, setHoveredProduct] = useState<Product | null>(null);
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(null);

  const mappableProducts = useMemo(
    () => products.filter(productHasMapLocation),
    [products],
  );

  const pinPoints = useMemo(
    (): LatLng[] =>
      mappableProducts.map(p => ({
        lat: p.lenderApproximateLatitude!,
        lng: p.lenderApproximateLongitude!,
      })),
    [mappableProducts],
  );

  const handleHoverProduct = useCallback((product: Product | null, pos: { x: number; y: number } | null) => {
    setHoveredProduct(product);
    setHoverPos(pos);
  }, []);

  const tileUrl = `${API_URL}/mapy/tiles/basic/{z}/{x}/{y}`;
  const radiusM = radiusKm * 1000;
  const thumbnail = hoveredProduct?.images?.[0]?.imageUrl;

  return (
    <div className={`search-results-map${loading ? ' search-results-map--loading' : ''}`}>
      <MapContainer
        center={[searchCenter.lat, searchCenter.lng]}
        zoom={12}
        className="search-results-map__canvas"
        scrollWheelZoom
        dragging
        doubleClickZoom
        touchZoom
      >
        <TileLayer
          url={tileUrl}
          attribution='&copy; <a href="https://mapy.com/">Mapy.com</a> &copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        <FitResultsView points={pinPoints} searchCenter={searchCenter} radiusKm={radiusKm} />
        <Circle
          center={[searchCenter.lat, searchCenter.lng]}
          radius={radiusM}
          pathOptions={{ color: '#2563eb', fillColor: '#3b82f6', fillOpacity: 0.06, weight: 1.5, dashArray: '4 4' }}
        />
        <ClusterMarkers
          products={mappableProducts}
          selectedProductId={selectedProductId}
          formatPrice={formatPrice}
          onSelectProduct={onSelectProduct}
          onHoverProduct={handleHoverProduct}
        />
      </MapContainer>

      {hoveredProduct && hoverPos && (
        <div
          className="map-product-card"
          style={{ left: hoverPos.x, top: hoverPos.y }}
          aria-hidden="true"
        >
          <div className="map-product-card__image">
            {thumbnail ? (
              <img src={thumbnail} alt="" />
            ) : (
              <div className="map-product-card__placeholder" />
            )}
          </div>
          <div className="map-product-card__body">
            <div className="map-product-card__name">{hoveredProduct.name}</div>
            <div className="map-product-card__price">{formatPrice(hoveredProduct)}</div>
          </div>
        </div>
      )}
    </div>
  );
};
